import { useState, useEffect, useCallback, useRef } from 'react';
import { NavHeader, type NavView } from './design-system/NavHeader';
import { Alert } from './design-system/Alert';
import { Tooltip } from './design-system/Tooltip';
import { useLanguage } from './context/LanguageContext';
import type { MessageKey } from './i18n/en';
import { BUILD_LABEL } from './version';
import { logError } from './lib/logger';
import { PerformanceAnalytics } from './analytics/PerformanceAnalytics';
import { TopMovers } from './analytics/TopMovers';
import styles from './App.module.css';

interface ConfigState {
  exchange: 'alpaca' | 'bybit';
  alpacaKey: string;
  alpacaSecret: string;
  geminiKey: string;
  paper: boolean;
  emaFast: number;
  emaSlow: number;
  rsiPeriod: number;
  rsiOverbought: number;
  rsiOversold: number;
  volPeriod: number;
  volMult: number;
  riskTrade: number;
  atrStop: number;
  maxPos: number;
  drawdown: number;
  maxTotalDrawdown: number;
  maxTradeSizeUsd: number;
  sentimentThresh: number;
  sentimentActive: boolean;
  strategyMode:
    | 'momentum'
    | 'scalping'
    | 'mean_reversion'
    | 'funding_arbitrage'
    | 'stat_arbitrage'
    | 'hybrid'
    | 'gitex_trade'
    | 'aggressive_koziel'
    | 'hft_arbitrage'
    | 'sentiment_momentum';
  telegramBotToken: string;
  telegramChatId: string;
  discordWebhookUrl: string;
  uptimerobotHeartbeatUrl: string;
  serverUrl: string;
  hftTakeProfitPct: number;
  hftStopLossPct: number;
}

interface AlpacaAccountInfo {
  equity: string;
  cash: string;
  buyingPower: string;
  profitLoss: string;
  profitLossPct: string;
}

interface AlpacaPositionInfo {
  symbol: string;
  side: string;
  qty: string;
  avg_entry_price: string;
  current_price: string;
  unrealized_pl: string;
  unrealized_plpc: string;
}

const DEFAULT_CONFIG: ConfigState = {
  exchange: 'alpaca',
  alpacaKey: '',
  alpacaSecret: '',
  geminiKey: '',
  paper: true,
  emaFast: 9,
  emaSlow: 21,
  rsiPeriod: 14,
  rsiOverbought: 70,
  rsiOversold: 30,
  volPeriod: 20,
  volMult: 1.5,
  riskTrade: 1.0,
  atrStop: 2.0,
  maxPos: 3,
  drawdown: 5.0,
  maxTotalDrawdown: 15.0,
  maxTradeSizeUsd: 1000,
  sentimentThresh: 0.0,
  sentimentActive: true,
  strategyMode: 'momentum',
  telegramBotToken: '',
  telegramChatId: '',
  discordWebhookUrl: '',
  uptimerobotHeartbeatUrl: '',
  serverUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000',
  hftTakeProfitPct: 1.2,
  hftStopLossPct: 0.6,
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Crypto symbols in this app are always written with a slash (e.g. "BTC/USD");
// stock tickers never contain one. Used to route a typed-or-picked symbol to
// the right watchlist bucket without asking the user to choose a class first.
export function inferAssetClass(symbol: string): 'crypto' | 'stocks' {
  return symbol.includes('/') ? 'crypto' : 'stocks';
}

// Maps a raw strategy_mode string (from the form or the server) to its
// localized display label — used both for the strategy cards and to show
// which strategy the engine is actually running vs. what's selected here.
const STRATEGY_LABEL_KEYS: Record<string, MessageKey> = {
  momentum: 'settings.strategyMomentum',
  scalping: 'settings.strategyScalping',
  funding_arbitrage: 'settings.strategyFunding',
  stat_arbitrage: 'settings.strategyStatArb',
  mean_reversion: 'settings.strategyMeanReversion',
  hybrid: 'settings.strategyHybrid',
  gitex_trade: 'settings.strategyGitexTrade',
  aggressive_koziel: 'settings.strategyAggressiveKoziel',
  hft_arbitrage: 'settings.strategyHftArbitrage',
  sentiment_momentum: 'settings.strategySentimentMomentum',
};

export function App(): JSX.Element {
  const { t } = useLanguage();
  const [view, setView] = useState<NavView>('dashboard');
  const [config, setConfig] = useState<ConfigState>(DEFAULT_CONFIG);
  const [successMsg, setSuccessMsg] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Live Telemetry state
  const [botActive, setBotActive] = useState<boolean>(true);
  // Whether the local engine server answered the last poll/toggle at all —
  // separate from botActive, so a stopped-locally engine process shows as
  // unreachable instead of leaving a stale "Active" status on screen.
  const [engineReachable, setEngineReachable] = useState<boolean>(true);
  const engineWasReachableRef = useRef(true);
  const [accountInfo, setAccountInfo] = useState<AlpacaAccountInfo | null>(null);
  const [positions, setPositions] = useState<AlpacaPositionInfo[]>([]);
  // The strategy_mode actually saved on the server — distinct from
  // config.strategyMode, which is just the (possibly unsaved) form
  // selection. Lets the Settings page warn when they've drifted apart, e.g.
  // a strategy card was clicked but "Apply & Save Settings" was never
  // pressed, so the engine is still trading on the old strategy.
  const [activeStrategyMode, setActiveStrategyMode] = useState<string | null>(null);
  // Live breaker telemetry + kill switch, polled from /api/risk/status.
  const [riskStatus, setRiskStatus] = useState<{
    kill_switch_on: boolean;
    daily_budget_used: number;
    daily_breaker_tripped: boolean;
    drawdown_budget_used: number;
    drawdown_breaker_tripped: boolean;
  } | null>(null);
  // FLATTEN ALL uses a two-click arm/confirm flow (no window.confirm allowed).
  const [flattenArmed, setFlattenArmed] = useState(false);
  const [flattening, setFlattening] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState<string | null>(null);
  const [serverConfigured, setServerConfigured] = useState<boolean>(false);
  const [sendingKeys, setSendingKeys] = useState<boolean>(false);
  const [closingPositions, setClosingPositions] = useState<Record<string, boolean>>({});

  // Watchlist state
  const [watchlist, setWatchlist] = useState<{ crypto: string[]; stocks: string[] }>({
    crypto: [],
    stocks: [],
  });
  const [smartWatchlistActive, setSmartWatchlistActive] = useState<boolean>(() => {
    return localStorage.getItem('gx_smart_watchlist_active') === 'true';
  });
  const [automatedWatchlist, setAutomatedWatchlist] = useState<{ crypto: string[]; stocks: string[] }>({
    crypto: [],
    stocks: [],
  });

  useEffect(() => {
    localStorage.setItem('gx_smart_watchlist_active', String(smartWatchlistActive));
    const syncSmartToggle = async () => {
      try {
        const saved = localStorage.getItem('gitextrade_config');
        const activeUrl = saved ? (JSON.parse(saved).serverUrl || API_BASE_URL) : API_BASE_URL;
        await fetch(`${activeUrl}/api/watchlist/smart-toggle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: smartWatchlistActive }),
        });
      } catch {
        // Degrade gracefully
      }
    };
    syncSmartToggle();
  }, [smartWatchlistActive]);
  const [newSymbol, setNewSymbol] = useState('');

  const [searchResults, setSearchResults] = useState<
    { symbol: string; name: string; exchange: string; asset_class: 'crypto' | 'stocks' }[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch matching assets for autocomplete search
  useEffect(() => {
    if (!newSymbol.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const query = encodeURIComponent(newSymbol.trim());
        // Searches crypto and stocks together — no asset-class picker needed.
        const res = await fetch(`${config.serverUrl}/api/assets/search?query=${query}`);
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.assets || []);
          setShowDropdown(true);
        } else {
          void logError(
            new Error(`Asset search returned HTTP ${res.status}`),
            'App.assetSearch',
          );
        }
      } catch (err) {
        // Previously swallowed silently — logged now so a broken search
        // (wrong serverUrl, CORS, engine down) is actually diagnosable
        // instead of the dropdown just never appearing with no trace.
        void logError(err, 'App.assetSearch');
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [newSymbol, config.serverUrl]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('gitextrade_config');
      if (saved) {
        setConfig({
          ...DEFAULT_CONFIG,
          ...JSON.parse(saved),
        });
      }
    } catch {
      // Degrade gracefully
    }
  }, []);

  // Load watchlist on mount
  useEffect(() => {
    const fetchWatchlist = async () => {
      try {
        const saved = localStorage.getItem('gitextrade_config');
        const activeUrl = saved ? (JSON.parse(saved).serverUrl || API_BASE_URL) : API_BASE_URL;
        const res = await fetch(`${activeUrl}/api/watchlist`);
        if (res.ok) {
          const data = await res.json();
          setWatchlist(data);
          if (data && typeof data.smart_watchlist_active === 'boolean') {
            setSmartWatchlistActive(data.smart_watchlist_active);
          }
        }
      } catch {
        // Degrade gracefully
      }
    };
    fetchWatchlist();
  }, []);

  // Stream or fetch automated watchlist
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupAutomatedWatchlist = async () => {
      try {
        const { db } = await import('./config/firebase');
        const { ref, onValue } = await import('firebase/database');

        if (db) {
          const hotRef = ref(db, 'watchlist/automated_hot');
          unsubscribe = onValue(hotRef, (snapshot) => {
            const val = snapshot.val();
            if (val) {
              setAutomatedWatchlist({
                crypto: val.crypto || [],
                stocks: val.stocks || [],
              });
            }
          });
          return;
        }
      } catch (err) {
        logError(err, 'Firebase streaming not available, falling back to REST');
      }

      const fetchAutomated = async () => {
        try {
          const saved = localStorage.getItem('gitextrade_config');
          const activeUrl = saved ? (JSON.parse(saved).serverUrl || API_BASE_URL) : API_BASE_URL;
          const res = await fetch(`${activeUrl}/api/watchlist/automated-hot`);
          if (res.ok) {
            const data = await res.json();
            setAutomatedWatchlist({
              crypto: data.crypto || [],
              stocks: data.stocks || [],
            });
          }
        } catch (err) {
          logError(err, 'Failed to fetch automated watchlist');
        }
      };

      fetchAutomated();
      const interval = setInterval(fetchAutomated, 60000);
      return () => clearInterval(interval);
    };

    const cleanupPromise = setupAutomatedWatchlist();

    return () => {
      if (unsubscribe) unsubscribe();
      cleanupPromise.then((cleanup) => {
        if (cleanup) cleanup();
      });
    };
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(false);
    try {
      const res = await fetch(`${config.serverUrl}/api/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alpaca_key: config.alpacaKey,
          alpaca_secret: config.alpacaSecret,
          alpaca_paper: config.paper,
          gemini_key: config.geminiKey,
          strategy_mode: config.strategyMode,
          max_total_drawdown: -Math.abs(config.maxTotalDrawdown) / 100,
          max_trade_size_usd: config.maxTradeSizeUsd,
          sentiment_active: config.sentimentActive,
          telegram_bot_token: config.telegramBotToken,
          telegram_chat_id: config.telegramChatId,
          discord_webhook_url: config.discordWebhookUrl,
          uptimerobot_heartbeat_url: config.uptimerobotHeartbeatUrl,
          ema_fast: config.emaFast,
          ema_slow: config.emaSlow,
          rsi_period: config.rsiPeriod,
          rsi_overbought: config.rsiOverbought,
          rsi_oversold: config.rsiOversold,
          hft_take_profit_pct: config.hftTakeProfitPct / 100,
          hft_stop_loss_pct: config.hftStopLossPct / 100,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.detail || 'Authorization failed: Invalid API credentials or Paper/Live mode mismatch.');
        setServerConfigured(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      localStorage.setItem('gitextrade_config', JSON.stringify(config));
      setServerConfigured(true);
      setSuccessMsg(true);
      setErrorMsg(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => setSuccessMsg(false), 5000);
    } catch {
      setErrorMsg('Failed to connect to the trading engine server.');
      setServerConfigured(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSendToServer = async () => {
    setSendingKeys(true);
    setErrorMsg(null);
    setSuccessMsg(false);
    try {
      const res = await fetch(`${config.serverUrl}/api/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          alpaca_key: config.alpacaKey,
          alpaca_secret: config.alpacaSecret,
          alpaca_paper: config.paper,
          gemini_key: config.geminiKey,
          strategy_mode: config.strategyMode,
          max_total_drawdown: -Math.abs(config.maxTotalDrawdown) / 100,
          max_trade_size_usd: config.maxTradeSizeUsd,
          sentiment_active: config.sentimentActive,
          telegram_bot_token: config.telegramBotToken,
          telegram_chat_id: config.telegramChatId,
          discord_webhook_url: config.discordWebhookUrl,
          uptimerobot_heartbeat_url: config.uptimerobotHeartbeatUrl,
          ema_fast: config.emaFast,
          ema_slow: config.emaSlow,
          rsi_period: config.rsiPeriod,
          rsi_overbought: config.rsiOverbought,
          rsi_oversold: config.rsiOversold,
          hft_take_profit_pct: config.hftTakeProfitPct / 100,
          hft_stop_loss_pct: config.hftStopLossPct / 100,
        }),
      });
      if (res.ok) {
        setServerConfigured(true);
        setSuccessMsg(true);
        setErrorMsg(null);
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.detail || 'Authorization failed: Invalid API credentials or Paper/Live mode mismatch.');
        setServerConfigured(false);
      }
    } catch {
      setErrorMsg('Failed to connect to the trading engine server.');
      setServerConfigured(false);
    } finally {
      setSendingKeys(false);
    }
  };

  const handleAddAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    const sym = newSymbol.trim().toUpperCase();
    if (!sym) return;

    try {
      const res = await fetch(`${config.serverUrl}/api/watchlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol: sym, asset_class: inferAssetClass(sym) }),
      });

      if (res.ok) {
        const data = await res.json();
        setWatchlist(data.watchlist);
        setNewSymbol('');
        setSuccessMsg(true);
        setTimeout(() => setSuccessMsg(false), 3000);
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.detail || 'Failed to add asset.');
        setTimeout(() => setErrorMsg(null), 5000);
      }
    } catch {
      setErrorMsg('Failed to connect to the trading engine server.');
      setTimeout(() => setErrorMsg(null), 5000);
    }
  };

  const handleRemoveAsset = async (symbol: string, assetClass: 'crypto' | 'stocks') => {
    try {
      const res = await fetch(`${config.serverUrl}/api/watchlist/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, asset_class: assetClass }),
      });

      if (res.ok) {
        const data = await res.json();
        setWatchlist(data.watchlist);
        setSuccessMsg(true);
        setTimeout(() => setSuccessMsg(false), 3000);
      } else {
        const errData = await res.json().catch(() => ({}));
        setErrorMsg(errData.detail || 'Failed to remove asset.');
        setTimeout(() => setErrorMsg(null), 5000);
      }
    } catch {
      setErrorMsg('Failed to connect to the trading engine server.');
      setTimeout(() => setErrorMsg(null), 5000);
    }
  };

  const isConnected = config.exchange === 'alpaca' && !!config.alpacaKey && !!config.alpacaSecret;

  // Poll Alpaca server API endpoints. Defined with useCallback (not inline in
  // the effect below) so handleToggleBot can also call it directly right
  // after a successful Start/Stop — otherwise positions/account info would
  // sit stale for up to 5s (the poll interval) until the next tick.
  const fetchTelemetry = useCallback(async () => {
      // 1. Bot active status — the actual "is the engine process reachable"
      // signal. Isolated from the two calls below: a hiccup on account/
      // positions must not incorrectly flip the whole engine to
      // unreachable and stop the trading-toggle button from working.
      try {
        const statusRes = await fetch(`${config.serverUrl}/api/bot/status`);
        if (statusRes.ok) {
          const data = await statusRes.json();
          setBotActive(data.active);
        }
        setEngineReachable(true);
        engineWasReachableRef.current = true;
      } catch (err) {
        // The local engine process may have been stopped — reflect that in
        // the UI instead of leaving a stale "Active" status on screen. Only
        // report the transition once (not every 5s poll) to avoid flooding
        // Sentry/RTDB while the engine stays down.
        setEngineReachable(false);
        setBotActive(false);
        if (engineWasReachableRef.current) {
          engineWasReachableRef.current = false;
          void logError(err, 'App.fetchTelemetry: trading engine unreachable');
        }
        return;
      }

      // 2. Account info — a failure here degrades gracefully; the next 5s
      // poll retries on its own, so it never needs a manual page refresh.
      try {
        const accRes = await fetch(`${config.serverUrl}/api/alpaca/account`);
        if (accRes.ok) {
          const data = await accRes.json();
          if (data.status === 'success') {
            setAccountInfo({
              equity: data.equity,
              cash: data.cash,
              buyingPower: data.buying_power,
              profitLoss: data.profit_loss,
              profitLossPct: data.profit_loss_pct,
            });
          }
        }
      } catch {
        // Degrade gracefully — isolated from bot-status/positions polling.
      }

      // 3. Positions — same isolation as account info above.
      try {
        const posRes = await fetch(`${config.serverUrl}/api/alpaca/positions`);
        if (posRes.ok) {
          const data = await posRes.json();
          if (data.status === 'success') {
            setPositions(data.positions || []);
          }
        }
      } catch {
        // Degrade gracefully — isolated from bot-status/account polling.
      }

      // 4. The strategy actually saved server-side — same isolation.
      try {
        const statusRes = await fetch(`${config.serverUrl}/api/config/status`);
        if (statusRes.ok) {
          const data = await statusRes.json();
          if (typeof data.strategy_mode === 'string') {
            setActiveStrategyMode(data.strategy_mode);
          }
        }
      } catch {
        // Degrade gracefully — isolated from the other polling calls.
      }

      // 5. Breaker telemetry + kill switch for the Emergency Risk Panel.
      try {
        const riskRes = await fetch(`${config.serverUrl}/api/risk/status`);
        if (riskRes.ok) {
          setRiskStatus(await riskRes.json());
        }
      } catch {
        // Degrade gracefully — isolated from the other polling calls.
      }
  }, [config.serverUrl]);

  useEffect(() => {
    if (!isConnected) {
      setAccountInfo(null);
      setPositions([]);
      return;
    }

    void fetchTelemetry();
    const timer = setInterval(fetchTelemetry, 5000);
    return () => clearInterval(timer);
  }, [isConnected, fetchTelemetry]);

  const handleToggleBot = async () => {
    try {
      const res = await fetch(`${config.serverUrl}/api/bot/toggle`, {
        method: 'POST',
      });
      if (res.ok) {
        const data = await res.json();
        setBotActive(data.active);
        setEngineReachable(true);
        engineWasReachableRef.current = true;
        // Refresh account/positions immediately — otherwise a just-started
        // session's positions would sit stale for up to 5s (the poll
        // interval) until the next automatic tick.
        void fetchTelemetry();
      } else {
        setErrorMsg(t('dashboard.toggleUnreachable'));
        setTimeout(() => setErrorMsg(null), 5000);
      }
    } catch (err) {
      // The engine may have been stopped locally — surface that instead of
      // the button silently doing nothing.
      setEngineReachable(false);
      setBotActive(false);
      if (engineWasReachableRef.current) {
        engineWasReachableRef.current = false;
        void logError(err, 'App.handleToggleBot: trading engine unreachable');
      }
      setErrorMsg(t('dashboard.toggleUnreachable'));
      setTimeout(() => setErrorMsg(null), 5000);
    }
  };

  const handleToggleKillSwitch = async () => {
    try {
      const res = await fetch(`${config.serverUrl}/api/emergency/kill-switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ on: !riskStatus?.kill_switch_on }),
      });
      if (res.ok) {
        const data = await res.json();
        setRiskStatus((prev) => (prev ? { ...prev, kill_switch_on: data.kill_switch_on } : prev));
      } else {
        setErrorMsg(t('dashboard.toggleUnreachable'));
        setTimeout(() => setErrorMsg(null), 5000);
      }
    } catch (err) {
      void logError(err, 'App.handleToggleKillSwitch');
      setErrorMsg(t('dashboard.toggleUnreachable'));
      setTimeout(() => setErrorMsg(null), 5000);
    }
  };

  const handleFlattenAll = async () => {
    // First click arms; the second click (within 5s) executes.
    if (!flattenArmed) {
      setFlattenArmed(true);
      setTimeout(() => setFlattenArmed(false), 5000);
      return;
    }
    setFlattenArmed(false);
    setFlattening(true);
    try {
      const res = await fetch(`${config.serverUrl}/api/emergency/flatten`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setBotActive(false);
        if (data.status === 'ok') {
          setNoticeMsg(t('risk.flattenDone'));
          setTimeout(() => setNoticeMsg(null), 8000);
        } else {
          setErrorMsg(t('risk.flattenPartial'));
          setTimeout(() => setErrorMsg(null), 8000);
        }
        void fetchTelemetry();
      } else {
        setErrorMsg(t('risk.flattenFailed'));
        setTimeout(() => setErrorMsg(null), 5000);
      }
    } catch (err) {
      void logError(err, 'App.handleFlattenAll');
      setErrorMsg(t('risk.flattenFailed'));
      setTimeout(() => setErrorMsg(null), 5000);
    } finally {
      setFlattening(false);
    }
  };

  const handleClosePosition = async (symbol: string) => {
    setClosingPositions((prev) => ({ ...prev, [symbol]: true }));
    setErrorMsg(null);
    try {
      const res = await fetch(`${config.serverUrl}/api/alpaca/positions/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ symbol }),
      });
      if (res.ok) {
        // Refresh positions list immediately
        const posRes = await fetch(`${config.serverUrl}/api/alpaca/positions`);
        if (posRes.ok) {
          const posData = await posRes.json();
          setPositions(posData.positions || []);
        }
      } else {
        const errorData = await res.json();
        setErrorMsg(`Failed to close position: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      setErrorMsg(`Error closing position: ${err}`);
    } finally {
      setClosingPositions((prev) => ({ ...prev, [symbol]: false }));
    }
  };

  // Helper to format currency
  const formatCurrency = (val: string | undefined): string => {
    if (!val) return '$0.00';
    const num = parseFloat(val);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  // Helper to format profit/loss percentage
  const formatPct = (val: string | undefined): string => {
    if (!val) return '0.00%';
    const num = parseFloat(val) * 100;
    if (isNaN(num)) return '0.00%';
    return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
  };

  const isPnlPositive = accountInfo && parseFloat(accountInfo.profitLoss) >= 0;

  if (view === 'momentum') {
    return <TopMovers serverUrl={config.serverUrl} onNavigate={setView} />;
  }

  return (
    <>
      <NavHeader active={view} onNavigate={setView} />
      <main style={{ padding: 'var(--space-6)' }}>
        <div className={styles.container}>
          {successMsg && (
            <Alert variant="success">{t('settings.saved')}</Alert>
          )}
          {errorMsg && (
            <Alert variant="danger">{errorMsg}</Alert>
          )}
          {noticeMsg && (
            <Alert variant="success">{noticeMsg}</Alert>
          )}

          {view === 'dashboard' && (
            <>
              {/* Status Header */}
              <div className={styles.statusCard}>
                <div className={styles.statusInfo}>
                  <span
                    className={`${styles.statusIndicator} ${
                      isConnected && botActive ? styles.statusActive : styles.statusInactive
                    }`}
                  />
                  <div>
                    <h3 className={styles.statusTitle}>{t('dashboard.status')}</h3>
                    <p className={styles.statusValue}>
                      {isConnected
                        ? botActive
                          ? t('dashboard.active')
                          : !engineReachable
                            ? t('dashboard.engineUnreachable')
                            : `${t('dashboard.inactive')} (Stopped)`
                        : t('dashboard.inactive')}
                    </p>
                  </div>
                  {isConnected && (
                    <button
                      type="button"
                      className={`${styles.botControlBtn} ${
                        botActive ? styles.botControlStop : styles.botControlStart
                      }`}
                      onClick={handleToggleBot}
                    >
                      {botActive ? t('dashboard.stopBot') : t('dashboard.startBot')}
                    </button>
                  )}
                </div>
                <div className={styles.pnlContainer}>
                  <h3 className={styles.statusTitle}>{t('dashboard.pnl')}</h3>
                  <span
                    className={`${styles.pnlValue} ${
                      isPnlPositive ? styles.pnlPositive : styles.pnlNegative
                    }`}
                  >
                    {accountInfo ? (isPnlPositive ? '+' : '') + formatCurrency(accountInfo.profitLoss) : '$0.00'}
                  </span>
                </div>
              </div>

              {!isConnected && (
                <Alert variant="info">{t('dashboard.engineIdle')}</Alert>
              )}

              {/* Emergency Risk Controls */}
              {isConnected && (
                <div className={styles.telemetryCard} style={{ marginBottom: 'var(--space-4)' }}>
                  <h3 className={styles.statusTitle}>{t('risk.panelTitle')}</h3>
                  {riskStatus?.kill_switch_on && (
                    <Alert variant="warning">{t('risk.killSwitchEngaged')}</Alert>
                  )}
                  <div className={styles.riskGaugeLabelRow}>
                    <span>{t('risk.dailyBudget')}</span>
                    <span>
                      {riskStatus ? `${Math.round(riskStatus.daily_budget_used * 100)}%` : '—'}
                      {riskStatus?.daily_breaker_tripped ? ` · ${t('risk.breakerTripped')}` : ''}
                    </span>
                  </div>
                  <div className={styles.riskGaugeTrack}>
                    <div
                      className={`${styles.riskGaugeFill} ${
                        (riskStatus?.daily_budget_used ?? 0) >= 1
                          ? styles.riskGaugeFillDanger
                          : (riskStatus?.daily_budget_used ?? 0) >= 0.6
                            ? styles.riskGaugeFillWarn
                            : ''
                      }`}
                      style={{
                        width: `${Math.min((riskStatus?.daily_budget_used ?? 0) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <div className={styles.riskGaugeLabelRow} style={{ marginTop: 'var(--space-2)' }}>
                    <span>{t('risk.drawdownBudget')}</span>
                    <span>
                      {riskStatus ? `${Math.round(riskStatus.drawdown_budget_used * 100)}%` : '—'}
                      {riskStatus?.drawdown_breaker_tripped ? ` · ${t('risk.breakerTripped')}` : ''}
                    </span>
                  </div>
                  <div className={styles.riskGaugeTrack}>
                    <div
                      className={`${styles.riskGaugeFill} ${
                        (riskStatus?.drawdown_budget_used ?? 0) >= 1
                          ? styles.riskGaugeFillDanger
                          : (riskStatus?.drawdown_budget_used ?? 0) >= 0.6
                            ? styles.riskGaugeFillWarn
                            : ''
                      }`}
                      style={{
                        width: `${Math.min((riskStatus?.drawdown_budget_used ?? 0) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <div className={styles.riskControlsRow}>
                    <button
                      type="button"
                      className={styles.closePositionBtn}
                      onClick={handleToggleKillSwitch}
                    >
                      {riskStatus?.kill_switch_on
                        ? t('risk.killSwitchOff')
                        : t('risk.killSwitchOn')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.botControlBtn} ${styles.botControlStop}`}
                      onClick={handleFlattenAll}
                      disabled={flattening}
                    >
                      {flattening
                        ? '⏳'
                        : flattenArmed
                          ? t('risk.flattenConfirm')
                          : t('risk.flatten')}
                    </button>
                  </div>
                </div>
              )}

              {/* Account Telemetry Row */}
              {isConnected && accountInfo && (
                <div className={styles.telemetryGrid}>
                  <div className={styles.telemetryCard}>
                    <div className={styles.telemetryLabel}>{t('dashboard.equity')}</div>
                    <div className={styles.telemetryValue}>{formatCurrency(accountInfo.equity)}</div>
                  </div>
                  <div className={styles.telemetryCard}>
                    <div className={styles.telemetryLabel}>{t('dashboard.buyingPower')}</div>
                    <div className={styles.telemetryValue}>{formatCurrency(accountInfo.buyingPower)}</div>
                  </div>
                </div>
              )}

              {/* Main Dashboard Grid */}
              <div className={styles.dashboardGrid}>
                {/* Left Side: Charts */}
                <div className={styles.mainContent}>
                  <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>{t('dashboard.charts')}</h2>
                    <div className={styles.chartsGrid}>
                      <div className={styles.chartWrapper}>
                        <iframe
                          title="BTC/USD Chart"
                          className={styles.chartFrame}
                          src="https://s.tradingview.com/widgetembed/?symbol=COINBASE:BTCUSD&theme=dark&interval=15&hidesidetoolbar=1&symboledit=0&saveimage=1&toolbarbg=f1f3f6&studies=[]&style=1&timezone=Etc%2FUTC"
                        />
                      </div>
                      <div className={styles.chartWrapper}>
                        <iframe
                          title="ETH/USD Chart"
                          className={styles.chartFrame}
                          src="https://s.tradingview.com/widgetembed/?symbol=COINBASE:ETHUSD&theme=dark&interval=15&hidesidetoolbar=1&symboledit=0&saveimage=1&toolbarbg=f1f3f6&studies=[]&style=1&timezone=Etc%2FUTC"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side: Active Rules Summary */}
                <div className={styles.sidebar}>
                  <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>{t('dashboard.summaryTitle')}</h2>
                    <div className={styles.rulesList}>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.exchange')}</span>
                        <span className={styles.ruleValue}>{config.exchange.toUpperCase()}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.emaFast')}</span>
                        <span className={styles.ruleValue}>{config.emaFast}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.emaSlow')}</span>
                        <span className={styles.ruleValue}>{config.emaSlow}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.rsiPeriod')}</span>
                        <span className={styles.ruleValue}>{config.rsiPeriod}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.rsiOverbought')}</span>
                        <span className={styles.ruleValue}>{config.rsiOverbought}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.rsiOversold')}</span>
                        <span className={styles.ruleValue}>{config.rsiOversold}</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.volMult')}</span>
                        <span className={styles.ruleValue}>{config.volMult}x</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.riskTrade')}</span>
                        <span className={styles.ruleValue}>{config.riskTrade}%</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.atrStop')}</span>
                        <span className={styles.ruleValue}>{config.atrStop}x</span>
                      </div>
                      <div className={styles.ruleItem}>
                        <span className={styles.ruleLabel}>{t('settings.drawdown')}</span>
                        <span className={styles.ruleValue}>{config.drawdown}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {view === 'positions' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('dashboard.positionsTitle')}</h2>
              {positions.length === 0 ? (
                <div className={styles.noPositionsMsg}>{t('dashboard.noPositions')}</div>
              ) : (
                <div className={styles.positionsTableWrapper}>
                  <table className={styles.positionsTable}>
                    <thead>
                      <tr>
                        <th>{t('dashboard.symbol')}</th>
                        <th>{t('dashboard.side')}</th>
                        <th>{t('dashboard.quantity')}</th>
                        <th>{t('dashboard.entryPrice')}</th>
                        <th>{t('dashboard.currentPrice')}</th>
                        <th>{t('dashboard.unrealizedPnl')}</th>
                        <th>{t('watchlist.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((pos) => {
                        const isLong = pos.side.toLowerCase() === 'long';
                        const profit = parseFloat(pos.unrealized_pl);
                        return (
                          <tr key={pos.symbol}>
                            <td style={{ fontWeight: 600 }}>{pos.symbol}</td>
                            <td className={isLong ? styles.sideLong : styles.sideShort}>
                              {pos.side}
                            </td>
                            <td>{parseFloat(pos.qty).toFixed(4)}</td>
                            <td>{formatCurrency(pos.avg_entry_price)}</td>
                            <td>{formatCurrency(pos.current_price)}</td>
                            <td className={profit >= 0 ? styles.pnlPositive : styles.pnlNegative}>
                              {profit >= 0 ? '+' : ''}{formatCurrency(pos.unrealized_pl)} ({formatPct(pos.unrealized_plpc)})
                            </td>
                            <td>
                              <button
                                type="button"
                                className={styles.closePositionBtn}
                                onClick={() => handleClosePosition(pos.symbol)}
                                disabled={closingPositions[pos.symbol]}
                              >
                                {closingPositions[pos.symbol] ? 'Closing...' : 'Close position'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <PerformanceAnalytics serverUrl={config.serverUrl} />
            </div>
          )}

          {view === 'settings' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('settings.title')}</h2>
              <form onSubmit={handleSave} className={styles.settingsForm}>
                
                {/* Credentials Section */}
                <div className={styles.settingsSection}>
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <label htmlFor="exchange">{t('settings.exchange')}</label>
                      <select
                        id="exchange"
                        className={styles.select}
                        value={config.exchange}
                        onChange={(e) => setConfig({ ...config, exchange: e.target.value as 'alpaca' | 'bybit' })}
                      >
                        <option value="alpaca">Alpaca</option>
                        <option value="bybit">Bybit</option>
                      </select>
                    </div>

                    <div className={styles.fieldGroup} style={{ justifyContent: 'center' }}>
                      <label className={styles.checkboxGroup}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={config.paper}
                          onChange={(e) => setConfig({ ...config, paper: e.target.checked })}
                        />
                        <span className={styles.checkboxLabel}>{t('settings.paper')}</span>
                      </label>
                    </div>
                  </div>

                  {config.exchange === 'alpaca' && (
                    <div className={styles.formGrid}>
                      <div className={styles.fieldGroup}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <label htmlFor="alpacaKey">{t('settings.alpacaKey')}</label>
                          <Tooltip translationKey="tooltip.alpacaKey" />
                        </div>
                        <input
                          id="alpacaKey"
                          type="text"
                          className={styles.input}
                          value={config.alpacaKey}
                          onChange={(e) => setConfig({ ...config, alpacaKey: e.target.value })}
                        />
                      </div>
                      <div className={styles.fieldGroup}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <label htmlFor="alpacaSecret">{t('settings.alpacaSecret')}</label>
                          <Tooltip translationKey="tooltip.alpacaSecret" />
                        </div>
                        <input
                          id="alpacaSecret"
                          type="password"
                          className={styles.input}
                          value={config.alpacaSecret}
                          onChange={(e) => setConfig({ ...config, alpacaSecret: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  {/* Strategy Mode Selection */}
                  <div style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                    <label style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', marginBottom: 'var(--space-2)' }}>
                      {t('settings.strategyMode')}
                    </label>
                    {activeStrategyMode && (
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-2)' }}>
                        {t('settings.activeStrategy')}:{' '}
                        <strong>
                          {t(STRATEGY_LABEL_KEYS[activeStrategyMode] ?? 'settings.strategyMomentum')}
                        </strong>
                      </p>
                    )}
                    {activeStrategyMode && activeStrategyMode !== config.strategyMode && (
                      <Alert variant="warning">{t('settings.unsavedStrategyWarning')}</Alert>
                    )}
                    <div className={styles.strategyCardsGrid}>
                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'gitex_trade' ? styles.strategyCardSelected : ''
                        }`}
                        style={{ gridColumn: '1 / -1', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(37, 99, 235, 0.02))' }}
                        onClick={() => setConfig({ ...config, strategyMode: 'gitex_trade' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ fontWeight: 700, color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyGitexTrade')}
                            <Tooltip translationKey="tooltip.strategyGitexTrade" />
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: 600, background: 'var(--color-primary)', color: '#fff', padding: '2px 6px', borderRadius: '4px', marginLeft: 'auto' }}>
                            ★ Recommended
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyGitexTradeDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'momentum' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'momentum' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyMomentum')}
                            <Tooltip translationKey="tooltip.strategyMomentum" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyMomentumDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'sentiment_momentum'
                            ? styles.strategyCardSelected
                            : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'sentiment_momentum' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategySentimentMomentum')}
                            <Tooltip translationKey="tooltip.strategySentimentMomentum" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategySentimentMomentumDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'scalping' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'scalping' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyScalping')}
                            <Tooltip translationKey="tooltip.strategyScalping" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyScalpingDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'aggressive_koziel' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'aggressive_koziel' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyAggressiveKoziel')}
                            <Tooltip translationKey="tooltip.strategyAggressiveKoziel" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyAggressiveKozielDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'hft_arbitrage' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'hft_arbitrage' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyHftArbitrage')}
                            <Tooltip translationKey="tooltip.strategyHftArbitrage" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyHftArbitrageDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'funding_arbitrage' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'funding_arbitrage' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyFunding')}
                            <Tooltip translationKey="tooltip.strategyFunding" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyFundingDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'stat_arbitrage' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'stat_arbitrage' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyStatArb')}
                            <Tooltip translationKey="tooltip.strategyStatArb" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyStatArbDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'mean_reversion' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'mean_reversion' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyMeanReversion')}
                            <Tooltip translationKey="tooltip.strategyMeanReversion" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyMeanReversionDesc')}
                        </div>
                      </div>

                      <div
                        className={`${styles.strategyCard} ${
                          config.strategyMode === 'hybrid' ? styles.strategyCardSelected : ''
                        }`}
                        onClick={() => setConfig({ ...config, strategyMode: 'hybrid' })}
                      >
                        <div className={styles.strategyCardHeader}>
                          <span className={styles.strategyRadio}>
                            <span className={styles.strategyRadioInner} />
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            {t('settings.strategyHybrid')}
                            <Tooltip translationKey="tooltip.strategyHybrid" />
                          </span>
                        </div>
                        <div className={styles.strategyDesc}>
                          {t('settings.strategyHybridDesc')}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gemini Key */}
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="geminiKey">{t('settings.geminiKey')}</label>
                        <Tooltip translationKey="tooltip.geminiKey" />
                      </div>
                      <input
                        id="geminiKey"
                        type="password"
                        className={styles.input}
                        value={config.geminiKey}
                        onChange={(e) => setConfig({ ...config, geminiKey: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                {/* Send to Server Section */}
                <h3 className={styles.sectionTitle}>{t('settings.serverSection')}</h3>
                <div className={styles.settingsSection}>
                  <p style={{ color: 'var(--color-muted)', fontSize: '0.875rem', marginBottom: 'var(--space-4)' }}>
                    {serverConfigured
                      ? t('settings.serverConfigured')
                      : t('settings.serverNotConfigured')}
                  </p>
                  <button
                    type="button"
                    id="sendToServerBtn"
                    className={styles.saveButton}
                    style={{
                      background: serverConfigured
                        ? 'linear-gradient(135deg, hsl(145, 70%, 40%), hsl(160, 65%, 35%))'
                        : 'linear-gradient(135deg, hsl(220, 80%, 55%), hsl(250, 75%, 50%))',
                      opacity: sendingKeys ? 0.7 : 1,
                      cursor: sendingKeys ? 'wait' : 'pointer',
                    }}
                    disabled={sendingKeys}
                    onClick={handleSendToServer}
                  >
                    {sendingKeys ? '⏳ Sending...' : serverConfigured ? '✓ ' + t('settings.sendToServer') : t('settings.sendToServer')}
                  </button>
                </div>

                {/* Strategy Section */}
                <h3 className={styles.sectionTitle}>{t('settings.strategyTitle')}</h3>

                <div className={styles.settingsSection}>
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="emaFast">{t('settings.emaFast')}</label>
                        <Tooltip translationKey="tooltip.emaFast" />
                      </div>
                      <input
                        id="emaFast"
                        type="number"
                        className={styles.input}
                        value={config.emaFast}
                        onChange={(e) => setConfig({ ...config, emaFast: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="emaSlow">{t('settings.emaSlow')}</label>
                        <Tooltip translationKey="tooltip.emaSlow" />
                      </div>
                      <input
                        id="emaSlow"
                        type="number"
                        className={styles.input}
                        value={config.emaSlow}
                        onChange={(e) => setConfig({ ...config, emaSlow: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="rsiPeriod">{t('settings.rsiPeriod')}</label>
                        <Tooltip translationKey="tooltip.rsiPeriod" />
                      </div>
                      <input
                        id="rsiPeriod"
                        type="number"
                        className={styles.input}
                        value={config.rsiPeriod}
                        onChange={(e) => setConfig({ ...config, rsiPeriod: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="rsiOverbought">{t('settings.rsiOverbought')}</label>
                        <Tooltip translationKey="tooltip.rsiOverbought" />
                      </div>
                      <input
                        id="rsiOverbought"
                        type="number"
                        className={styles.input}
                        value={config.rsiOverbought}
                        onChange={(e) => setConfig({ ...config, rsiOverbought: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="rsiOversold">{t('settings.rsiOversold')}</label>
                        <Tooltip translationKey="tooltip.rsiOversold" />
                      </div>
                      <input
                        id="rsiOversold"
                        type="number"
                        className={styles.input}
                        value={config.rsiOversold}
                        onChange={(e) => setConfig({ ...config, rsiOversold: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="volPeriod">{t('settings.volPeriod')}</label>
                        <Tooltip translationKey="tooltip.volPeriod" />
                      </div>
                      <input
                        id="volPeriod"
                        type="number"
                        className={styles.input}
                        value={config.volPeriod}
                        onChange={(e) => setConfig({ ...config, volPeriod: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="volMult">{t('settings.volMult')}</label>
                        <Tooltip translationKey="tooltip.volMult" />
                      </div>
                      <input
                        id="volMult"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.volMult}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="hftTakeProfitPct">{t('settings.hftTakeProfitPct')}</label>
                        <Tooltip translationKey="tooltip.hftTakeProfitPct" />
                      </div>
                      <input
                        id="hftTakeProfitPct"
                        type="number"
                        step="0.001"
                        className={styles.input}
                        value={config.hftTakeProfitPct}
                        onChange={(e) => setConfig({ ...config, hftTakeProfitPct: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="hftStopLossPct">{t('settings.hftStopLossPct')}</label>
                        <Tooltip translationKey="tooltip.hftStopLossPct" />
                      </div>
                      <input
                        id="hftStopLossPct"
                        type="number"
                        step="0.001"
                        className={styles.input}
                        value={config.hftStopLossPct}
                        onChange={(e) => setConfig({ ...config, hftStopLossPct: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                </div>

                {/* Risk Section */}
                <h3 className={styles.sectionTitle}>{t('settings.riskTitle')}</h3>
                <div className={styles.settingsSection}>
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="riskTrade">{t('settings.riskTrade')}</label>
                        <Tooltip translationKey="tooltip.riskTrade" />
                      </div>
                      <input
                        id="riskTrade"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.riskTrade}
                        onChange={(e) => setConfig({ ...config, riskTrade: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="atrStop">{t('settings.atrStop')}</label>
                        <Tooltip translationKey="tooltip.atrStop" />
                      </div>
                      <input
                        id="atrStop"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.atrStop}
                        onChange={(e) => setConfig({ ...config, atrStop: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="maxPos">{t('settings.maxPos')}</label>
                        <Tooltip translationKey="tooltip.maxPos" />
                      </div>
                      <input
                        id="maxPos"
                        type="number"
                        className={styles.input}
                        value={config.maxPos}
                        onChange={(e) => setConfig({ ...config, maxPos: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="drawdown">{t('settings.drawdown')}</label>
                        <Tooltip translationKey="tooltip.drawdown" />
                      </div>
                      <input
                        id="drawdown"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.drawdown}
                        onChange={(e) => setConfig({ ...config, drawdown: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="maxTotalDrawdown">{t('settings.maxTotalDrawdown')}</label>
                        <Tooltip translationKey="tooltip.maxTotalDrawdown" />
                      </div>
                      <input
                        id="maxTotalDrawdown"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.maxTotalDrawdown}
                        onChange={(e) =>
                          setConfig({ ...config, maxTotalDrawdown: parseFloat(e.target.value) || 0 })
                        }
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="maxPosSize">{t('settings.maxPosSize')}</label>
                        <Tooltip translationKey="tooltip.maxPosSize" />
                      </div>
                      <input
                        id="maxPosSize"
                        type="number"
                        className={styles.input}
                        value={config.maxTradeSizeUsd}
                        onChange={(e) =>
                          setConfig({ ...config, maxTradeSizeUsd: parseFloat(e.target.value) || 0 })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Sentiment Section */}
                <h3 className={styles.sectionTitle}>{t('settings.sentimentTitle')}</h3>
                <div className={styles.settingsSection}>
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="sentimentThresh">{t('settings.sentimentThresh')}</label>
                        <Tooltip translationKey="tooltip.sentimentThresh" />
                      </div>
                      <input
                        id="sentimentThresh"
                        type="number"
                        step="0.1"
                        className={styles.input}
                        value={config.sentimentThresh}
                        onChange={(e) => setConfig({ ...config, sentimentThresh: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div className={styles.fieldGroup} style={{ justifyContent: 'center' }}>
                      <label className={styles.checkboxGroup}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={config.sentimentActive}
                          onChange={(e) => setConfig({ ...config, sentimentActive: e.target.checked })}
                        />
                        <span className={styles.checkboxLabel}>
                          {t('settings.sentimentActive')}
                          <Tooltip translationKey="tooltip.sentimentActive" />
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Alerts & Monitoring Section */}
                <h3 className={styles.sectionTitle}>{t('settings.alertsTitle')}</h3>
                <div className={styles.settingsSection}>
                  <p style={{ color: 'var(--color-muted)', fontSize: '0.875rem', marginBottom: 'var(--space-4)' }}>
                    {t('settings.alertsDesc')}
                  </p>
                  <div className={styles.formGrid}>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="telegramBotToken">{t('settings.telegramBotToken')}</label>
                        <Tooltip translationKey="tooltip.telegramBotToken" />
                      </div>
                      <input
                        id="telegramBotToken"
                        type="password"
                        className={styles.input}
                        value={config.telegramBotToken}
                        onChange={(e) => setConfig({ ...config, telegramBotToken: e.target.value })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="telegramChatId">{t('settings.telegramChatId')}</label>
                        <Tooltip translationKey="tooltip.telegramChatId" />
                      </div>
                      <input
                        id="telegramChatId"
                        type="text"
                        className={styles.input}
                        value={config.telegramChatId}
                        onChange={(e) => setConfig({ ...config, telegramChatId: e.target.value })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="discordWebhookUrl">{t('settings.discordWebhookUrl')}</label>
                        <Tooltip translationKey="tooltip.discordWebhookUrl" />
                      </div>
                      <input
                        id="discordWebhookUrl"
                        type="password"
                        className={styles.input}
                        value={config.discordWebhookUrl}
                        onChange={(e) => setConfig({ ...config, discordWebhookUrl: e.target.value })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="uptimerobotHeartbeatUrl">{t('settings.uptimerobotHeartbeatUrl')}</label>
                        <Tooltip translationKey="tooltip.uptimerobotHeartbeatUrl" />
                      </div>
                      <input
                        id="uptimerobotHeartbeatUrl"
                        type="text"
                        className={styles.input}
                        value={config.uptimerobotHeartbeatUrl}
                        onChange={(e) => setConfig({ ...config, uptimerobotHeartbeatUrl: e.target.value })}
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <label htmlFor="serverUrl">{t('settings.serverUrl')}</label>
                        <Tooltip translationKey="tooltip.serverUrl" />
                      </div>
                      <input
                        id="serverUrl"
                        type="text"
                        className={styles.input}
                        value={config.serverUrl}
                        onChange={(e) => setConfig({ ...config, serverUrl: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                </div>

                <button type="submit" className={styles.saveButton}>
                  {t('settings.save')}
                </button>
              </form>
            </div>
          )}

          {view === 'watchlist' && (
            <div className={styles.watchlistContainer}>
              <div className={styles.smartWatchlistCard}>
                <label className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={smartWatchlistActive}
                    onChange={(e) => setSmartWatchlistActive(e.target.checked)}
                  />
                  <span className={styles.smartWatchlistLabel}>
                    {t('watchlist.smartActive')}
                    <Tooltip translationKey="tooltip.smartActive" />
                  </span>
                </label>
                <div className={styles.smartWatchlistBadge}>
                  Top 100 Crypto / Top 1000 Stocks Safety Filter
                </div>
              </div>

              {smartWatchlistActive && (
                <div className={styles.smartWatchlistActiveMsg}>
                  {t('watchlist.smartActiveMsg')}
                </div>
              )}

              <div className={styles.watchlistGrid}>
                {/* Add Asset Card */}
                <div className={`${styles.watchlistAddCard} ${smartWatchlistActive ? styles.disabledSection : ''}`}>
                  <h2 className={styles.sectionTitle}>{t('watchlist.addAsset')}</h2>
                  <form onSubmit={handleAddAsset} className={styles.settingsForm}>
                    <div className={styles.fieldGroup}>
                      <label htmlFor="symbol">{t('watchlist.symbol')}</label>
                      <div className={styles.searchWrapper}>
                        <input
                          id="symbol"
                          type="text"
                          placeholder="e.g. SOL/USD or TSLA"
                          className={styles.input}
                          value={newSymbol}
                          onChange={(e) => {
                            setNewSymbol(e.target.value);
                            setShowDropdown(true);
                          }}
                          onFocus={() => {
                            if (newSymbol.trim()) {
                              setShowDropdown(true);
                            }
                          }}
                          onBlur={() => {
                            // Delay slightly so that click event on dropdown items goes through first
                            setTimeout(() => setShowDropdown(false), 200);
                          }}
                          autoComplete="off"
                          required
                          disabled={smartWatchlistActive}
                          style={{ paddingRight: '36px' }}
                        />
                        {searchLoading && (
                          <div style={{
                            position: 'absolute',
                            right: '12px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: '12px',
                            color: 'var(--color-primary)',
                            pointerEvents: 'none',
                          }}>
                            ⏳
                          </div>
                        )}
                        {showDropdown && searchResults.length > 0 && (
                          <ul className={styles.searchDropdown}>
                            {searchResults.map((asset) => (
                              <li
                                key={asset.symbol}
                                className={styles.searchItem}
                                onClick={() => {
                                  setNewSymbol(asset.symbol);
                                  setShowDropdown(false);
                                }}
                              >
                                <span className={styles.searchItemSymbol}>
                                  {asset.symbol}
                                  <span className={styles.searchItemExchange}>
                                    {asset.exchange}
                                  </span>
                                </span>
                                <span className={styles.searchItemName}>
                                  {asset.name}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                    <button type="submit" className={styles.saveButton} disabled={smartWatchlistActive}>
                      {t('watchlist.addButton')}
                    </button>
                  </form>
                </div>

                {/* Lists Card */}
                <div className={styles.watchlistTableCard}>
                  <h2 className={styles.sectionTitle}>{t('watchlist.title')}</h2>
                  
                  {/* Crypto watchlist */}
                  <h3 style={{ marginTop: '0', marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-md)' }}>
                    {t('watchlist.crypto')}
                  </h3>
                  {(smartWatchlistActive ? automatedWatchlist.crypto.length : watchlist.crypto.length) === 0 ? (
                    <div className={styles.noPositionsMsg} style={{ marginBottom: 'var(--space-4)' }}>
                      {t('watchlist.empty')}
                    </div>
                  ) : (
                    <div className={styles.positionsTableWrapper} style={{ marginBottom: 'var(--space-6)' }}>
                      <table className={styles.positionsTable}>
                        <thead>
                          <tr>
                            <th>{t('watchlist.symbol')}</th>
                            <th style={{ textAlign: 'right' }}>{t('watchlist.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(smartWatchlistActive ? automatedWatchlist.crypto : watchlist.crypto).map((symbol) => (
                            <tr key={symbol}>
                              <td style={{ fontWeight: 600 }}>{symbol}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className={styles.watchlistRemoveBtn}
                                  onClick={() => handleRemoveAsset(symbol, 'crypto')}
                                  disabled={smartWatchlistActive}
                                  style={smartWatchlistActive ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                >
                                  {t('watchlist.remove')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Stocks watchlist */}
                  <h3 style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-md)' }}>
                    {t('watchlist.stocks')}
                  </h3>
                  {(smartWatchlistActive ? automatedWatchlist.stocks.length : watchlist.stocks.length) === 0 ? (
                    <div className={styles.noPositionsMsg}>{t('watchlist.empty')}</div>
                  ) : (
                    <div className={styles.positionsTableWrapper}>
                      <table className={styles.positionsTable}>
                        <thead>
                          <tr>
                            <th>{t('watchlist.symbol')}</th>
                            <th style={{ textAlign: 'right' }}>{t('watchlist.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(smartWatchlistActive ? automatedWatchlist.stocks : watchlist.stocks).map((symbol) => (
                            <tr key={symbol}>
                              <td style={{ fontWeight: 600 }}>{symbol}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  type="button"
                                  className={styles.watchlistRemoveBtn}
                                  onClick={() => handleRemoveAsset(symbol, 'stocks')}
                                  disabled={smartWatchlistActive}
                                  style={smartWatchlistActive ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                >
                                  {t('watchlist.remove')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <footer className={styles.buildFooter}>
        {t('footer.build')} {BUILD_LABEL}
      </footer>
    </>
  );
}
